/**
 * @file useComicPages.js
 * @description 漫画阅读页 — 当前卷图片分页加载
 * @module features/comic/composables/useComicPages
 */

import { fetchVolPagesApi } from '../api/comicApi'
import { findVolByNum, VOL_ACCESS_STATUS } from '../utils/volAccess'
import {
  applyLockedVolPreviewImages,
  clearReaderPageImages,
  resolveVolPagesAccess,
  resolveVolPagesResult,
} from '../utils/handleVolPagesResponse'
import { openVolPay } from './useVolPay'

/**
 * @param {Record<string, Function>} bridge 共享方法桥（避免循环依赖）
 * @param {object} ctx reactive 状态
 * @param {import('vue').Ref<HTMLElement|null>} [_readerContainerRef] 保留参数位，与 useComicScroll 对齐
 */
export function useComicPages(bridge, ctx, _readerContainerRef) {
  function setImageRef(el, index) {
    if (el) {
      ctx.imageRefs[index] = el
    }
  }

  function setLoadedVolumeImageRef(el, vol, imgIndex) {
    if (el) {
      const globalIndex = vol.startIndex + imgIndex
      if (!ctx.imageRefs[globalIndex]) {
        ctx.imageRefs[globalIndex] = el
      }
    }
  }

  /**
   * 未解锁当前卷：一屏预览并弹登录或购卷，不恢复进度、不拉下一页。
   * @param {object} data vol/all data
   * @param {number} requestVolNum
   * @param {import('../utils/volAccess').VolAccessStatus} access
   */
  function gateLockedCurrentVolume(data, requestVolNum, access) {
    applyLockedVolPreviewImages(ctx, data)
    if (access === VOL_ACCESS_STATUS.NEED_LOGIN) {
      ctx.$emit('show-login-popup')
      return
    }
    const vol = findVolByNum(ctx.volInfoVos, requestVolNum)
    const volId =
      data.volId ||
      vol?.id ||
      (typeof bridge.getVolIdFromVolInfoVos === 'function'
        ? bridge.getVolIdFromVolInfoVos(requestVolNum)
        : null)
    if (!volId) return
    openVolPay({
      comicId: String(ctx.comicId),
      volId: String(volId),
      volNum: requestVolNum,
      onUnlocked: () => {
        ctx.pagesLocked = false
        ctx.isLoading = false
        void fetchComicPages(1, false, requestVolNum)
      },
    })
  }

  async function fetchComicPages(pageNum = 1, append = false, explicitVolNum = null) {
    // 如果正在加载，避免重复请求（卷切换时会重置 isLoading）
    if (ctx.isLoading) {
            return
    }

    const fetchToken = ctx.pagesFetchToken
    const requestVolNum = explicitVolNum != null
      ? Number(explicitVolNum)
      : Number(ctx.$route?.params?.volNum ?? ctx.volNum)

    try {
      ctx.isLoading = true
      const response = await fetchVolPagesApi(
        {
          comicId: ctx.comicId,
          volNum: requestVolNum,
          pageNum,
          pageSize: ctx.pageSize,
        },
        ctx.token
      )

      if (fetchToken !== ctx.pagesFetchToken || requestVolNum !== Number(ctx.$route?.params?.volNum ?? ctx.volNum)) {
        console.warn('[VolSwitch] fetchComicPages 丢弃过期响应', {
          requestVolNum,
          routeVol: ctx.$route?.params?.volNum,
          ctxVol: ctx.volNum,
          fetchToken,
          currentToken: ctx.pagesFetchToken,
        })
        return
      }

      if (response.data.code === 200 || response.data.code === 0) {
        const data = response.data.data || {}
        const access = resolveVolPagesAccess(data, { hasToken: Boolean(ctx.token) })
        if (access === VOL_ACCESS_STATUS.LOCKED || access === VOL_ACCESS_STATUS.NEED_LOGIN) {
          gateLockedCurrentVolume(data, requestVolNum, access)
          return
        }

        ctx.pagesLocked = false
        const imgUrls = data.imgUrls || []

        // 更新分页信息
        ctx.currentPageNum = data.pageNum || pageNum
        ctx.pageSize = data.pageSize || ctx.pageSize
        ctx.totalPages = data.totalPages || 0
        ctx.totalCount = data.totalCount || 0
        ctx.hasNext = data.hasNext || false
        ctx.hasPrev = data.hasPrev || false

        // 如果是追加模式，将新图片追加到数组；否则替换
        if (append) {
          ctx.pages = [...ctx.pages, ...imgUrls]
        } else {
          ctx.pages = imgUrls
          // 重置分页状态
          ctx.currentPageNum = 1
          // 首次加载时，重置互动数据
          ctx.interactData = null
          ctx.volId = data.volId || bridge.getVolIdFromVolInfoVos(requestVolNum)
                  }

        // 首次加载卷内容时（非追加模式），立即获取互动数据
        if (!append) {

          // 如果已经有 volId，直接获取互动数据；否则先获取 volId
          if (ctx.volId) {
                        // 异步获取，不阻塞页面渲染
            bridge.fetchInteractData().catch(err => {
              console.error('获取互动数据失败:', err)
            })
          } else {
            console.warn('首次加载，没有 volId，无法获取互动数据')
            console.warn('volInfoVos:', ctx.volInfoVos)
            console.warn('当前 volNum:', ctx.volNum)
          }
        }

        // 如果当前卷已加载完成（没有下一页），确保互动数据已加载，并标记卷完成
        if (!ctx.hasNext) {
          // 如果还没有 volId，尝试从 volInfoVos 中获取
          if (!ctx.volId) {
            ctx.volId = data.volId || bridge.getVolIdFromVolInfoVos(Number(ctx.volNum))
                      }

          // 如果已经有 volId，直接获取互动数据
          if (ctx.volId && !ctx.interactData && !ctx.isLoadingInteract) {
                        await bridge.fetchInteractData().then(() => {
              // 互动数据加载完成后，标记当前卷已完成
              bridge.checkCurrentVolumeComplete()
            })
          } else if (ctx.interactData) {
            // 如果已经有互动数据，直接标记完成
            bridge.checkCurrentVolumeComplete()
          } else {
            console.warn('卷加载完成，没有 volId，无法获取互动数据')
            // 即使没有互动数据，如果图片已加载完成，也标记为完成（允许继续）
            ctx.isCurrentVolumeComplete = true
          }
        } else {
          // 还有下一页，当前卷未完成
          ctx.isCurrentVolumeComplete = false
        }

        // 等待 DOM 更新后初始化进度记录和恢复
        ctx.$nextTick(async () => {
          if (!append) {
            const hasPageIndex = ctx.$route.query.pageIndex !== undefined
            const shouldRestore = hasPageIndex || ctx.$route.query.restoreProgress === 'true' || ctx.$route.query.restoreProgress === true
            if (shouldRestore) {
                            await bridge.restoreProgress()
            } else {
                            await bridge.waitForReaderReady()
              bridge.resetScrollPosition()
              ctx.maxSavedPageIndex = -1
              bridge.initProgressTracking()
            }
          } else if (!ctx.isRestoringProgress) {
            bridge.initProgressTracking()
          }
        })
      } else {
        const result = resolveVolPagesResult(response.data.code)
        if (result === 'need-login') {
          applyLockedVolPreviewImages(ctx, response.data?.data)
          ctx.$emit('show-login-popup')
        } else if (result === 'need-pay') {
          gateLockedCurrentVolume(response.data?.data || {}, requestVolNum, VOL_ACCESS_STATUS.LOCKED)
        } else {
          clearReaderPageImages(ctx)
        }
      }
    } catch (error) {
      console.error('请求漫画内容失败:', error)
    } finally {
      if (fetchToken === ctx.pagesFetchToken) {
        ctx.isLoading = false
      }
    }
  }

  async function loadNextPage() {
    if (!ctx.hasNext || ctx.isLoading || ctx.pagesLocked) {
      return
    }

    const nextPageNum = ctx.currentPageNum + 1
    await fetchComicPages(nextPageNum, true) // append = true 表示追加数据
    // 加载完成后，重置触发索引，允许下次加载
    // 注意：这里不重置，因为lastLoadTriggerIndex应该基于当前页的索引
  }

  Object.assign(bridge, {
    setImageRef,
    setLoadedVolumeImageRef,
    fetchComicPages,
    loadNextPage,
  })
}
